// react
import { MouseEvent, useContext, useState } from 'react';

// types
import { ActionButton, IfrPlanVersion, IfrPlanVersionPayload } from '../../../@types';

// i18n
import { Trans, useTranslation } from 'react-i18next';

// context
import { ContextIfrPlan, ContextIfrPlanVersion } from '../../pages/Project/Project.context';

// hooks
import { useIfr } from '../../../hooks/useIfr';

// mui
import { Alert, Grid, Typography } from '@mui/material';

// dialogs
import { DialogActions } from '../DialogActions/DialogActions';

// props
export interface DialogAddIfrPlanVersionProps {
	isOpen: boolean;
	onClose: (e?: MouseEvent<HTMLButtonElement | HTMLAnchorElement>) => void;
	onSuccess?: ({ ifrPlanVersion }: { ifrPlanVersion: IfrPlanVersion }) => void;
	ifrPlanVersionPayload?: IfrPlanVersionPayload | null;
}

export const DialogAddIfrPlanVersion = ({
	isOpen,
	onClose,
	onSuccess,
	ifrPlanVersionPayload,
}: DialogAddIfrPlanVersionProps) => {
	// hooks
	const { t } = useTranslation();
	const { postIfrPlanVersion, status } = useIfr();

	// context
	const { ifrPlan } = useContext(ContextIfrPlan);
	const { ifrPlanVersion } = useContext(ContextIfrPlanVersion);

	// state
	const [error, setError] = useState<string | null>(null);

	// dialog content
	const dialog = {
		alert: t('alert.createNewIfrPlanVersion'),
		btnSubmit: {
			children: t('createNewDraft'),
			color: 'primary',
		},
		i18nKey: 'dialogCreateIfrPlanVersion.content',
		title: t('editPlan'),
	};

	// handlers
	const handleSubmit = (e: MouseEvent<HTMLButtonElement | HTMLAnchorElement>) => {
		// clear error
		setError(null);

		if (!ifrPlan || !ifrPlanVersion || !ifrPlanVersionPayload?.data) {
			setError(t('error.default'));
			return;
		}

		// create new ifr version based on finalized version
		postIfrPlanVersion({
			ifrPlanId: ifrPlan.id,
			ifrPlanVersionPayload: {
				data: ifrPlanVersionPayload.data,
				status: 'DRAFT',
			},
			ifrPlanVersionId: ifrPlanVersion.id,
			onSuccess: ({ ifrPlanVersion: ifrPlanVersionUpdated }) => {
				if (onSuccess) {
					onSuccess({ ifrPlanVersion: ifrPlanVersionUpdated });
				}

				// close dialog
				if (onClose) {
					onClose();
				}
			},
		});
	};

	const handleClose = (e: MouseEvent<HTMLButtonElement | HTMLAnchorElement>) => {
		// clear error
		setError(null);

		if (onClose) {
			onClose(e);
		}
	};

	return (
		<DialogActions
			actions={[
				{
					children: t('cancel'),
					onClick: handleClose,
					variant: 'text',
				},
				{
					...(dialog.btnSubmit as ActionButton),
					loading: status === 'LOADING',
					onClick: handleSubmit,
					variant: 'contained',
				},
			]}
			alignItems="center"
			IconCircleProps={{
				type: 'plan',
			}}
			isOpen={isOpen}
			maxWidth={360}
			onClose={handleClose}
			subtitle={{
				children: ifrPlan?.name,
			}}
			title={{
				children: dialog.title,
			}}>
			<Grid container spacing={2}>
				<Grid item xs={12}>
					<Trans
						components={{
							strong: <Typography color="brand.blue.600" component="span" fontWeight={600} />,
						}}
						i18nKey={dialog.i18nKey}
						values={{ name: ifrPlan?.name }}
					/>
				</Grid>
				<Grid item xs={12}>
					<Alert severity="warning">{dialog.alert}</Alert>
				</Grid>
				{error && (
					<Grid item xs={12}>
						<Alert severity="error">{error}</Alert>
					</Grid>
				)}
			</Grid>
		</DialogActions>
	);
};
